import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { LocalOut } from '@/types/api';
import { isOrganizer, isSuperAdmin } from '@/utils/roleUtils';

/**
 * Resolves which Local's shared warehouse stock to show.
 *
 * Warehouse staff are pinned to whichever Local their assigned event belongs
 * to. Organizers/super admins aren't tied to a single event, so they instead
 * pick from the Locals they manage (`availableLocals` is non-empty only for
 * them — used by pages to decide whether to render a Local picker).
 */
export function useWarehouseLocal() {
  const { user } = useAuth();
  const [localId, setLocalId] = useState<number | null>(null);
  const [availableLocals, setAvailableLocals] = useState<LocalOut[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      setLoading(true);
      try {
        if (isOrganizer(user) || isSuperAdmin(user)) {
          const localsData = await apiService.getLocals();
          if (cancelled) return;
          setAvailableLocals(localsData);
          setLocalId((prev) => (prev && localsData.some((l) => l.id === prev) ? prev : localsData[0]?.id ?? null));
        } else if (user?.event_id) {
          const event = await apiService.getEvent(user.event_id);
          if (cancelled) return;
          setAvailableLocals([]);
          setLocalId(event.local_id ?? null);
        } else {
          setAvailableLocals([]);
          setLocalId(null);
        }
      } catch (err) {
        console.error('Failed to resolve warehouse Local:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.role, user?.event_id]);

  return { localId, setLocalId, availableLocals, loading };
}
